/**
 * Cart Store
 * Verwaltet Warenkorb-Zustand
 */

import { writable, derived } from 'svelte/store';
import type { CartItem, Product } from '../types';
import { formatCurrency } from '../utils/format';

interface CartState {
  items: CartItem[];
}

function createCartStore() {
  const { subscribe, set, update } = writable<CartState>({
    items: []
  });

  return {
    subscribe,

    /**
     * Fügt Produkt zum Warenkorb hinzu
     * Registrierkassen-Logik: Nur wenn Artikelnummer UND Preis gleich sind, wird Menge erhöht
     * Sonst wird eine neue Zeile hinzugefügt
     */
    add(product: Product, quantity: number = 1, price?: number): void {
      const finalPrice = price ?? product.price;
      
      update((state) => {
        // Suche nach existierendem Item mit gleicher Artikelnummer UND gleichem Preis
        const existingItem = state.items.find(
          (item) => item.product.item_code === product.item_code && item.price === finalPrice
        );

        if (existingItem) {
          // Produkt mit gleicher Artikelnummer UND Preis bereits im Warenkorb: Menge erhöhen
          const newQuantity = existingItem.quantity + quantity;
          const newSubtotal = newQuantity * existingItem.price;
          return {
            items: state.items.map((item) =>
              item.id === existingItem.id
                ? {
                    ...item,
                    quantity: newQuantity,
                    subtotal: newSubtotal
                  }
                : item
            )
          };
        } else {
          // Neue Zeile hinzufügen (auch wenn Produkt mit anderem Preis bereits existiert)
          const newItem: CartItem = {
            id: `${product.item_code}-${finalPrice}-${Date.now()}`, // Eindeutige ID
            product,
            quantity,
            price: finalPrice,
            subtotal: quantity * finalPrice
          };
          return {
            items: [...state.items, newItem]
          };
        }
      });
    },

    /**
     * Entfernt Item aus Warenkorb (anhand der CartItem ID)
     */
    remove(itemId: string): void {
      update((state) => ({
        items: state.items.filter((item) => item.id !== itemId)
      }));
    },

    /**
     * Aktualisiert Menge eines Items (anhand der CartItem ID)
     */
    updateQuantity(itemId: string, quantity: number): void {
      if (quantity <= 0) {
        this.remove(itemId);
        return;
      }

      update((state) => ({
        items: state.items.map((item) =>
          item.id === itemId
            ? {
                ...item,
                quantity,
                subtotal: quantity * item.price
              }
            : item
        )
      }));
    },

    /**
     * Aktualisiert Preis eines Items
     */
    updatePrice(itemId: string, price: number): void {
      if (price <= 0) {
        this.remove(itemId);
        return;
      }

      update((state) => ({
        items: state.items.map((item) =>
          item.id === itemId
            ? {
                ...item,
                price,
                subtotal: item.quantity * price
              }
            : item
        )
      }));
    },

    /**
     * Setzt Rabatt für Item
     */
    setDiscount(itemId: string, discount: number): void {
      update((state) => ({
        items: state.items.map((item) => {
          if (item.id === itemId) {
            const discountAmount = (item.price * item.quantity * discount) / 100;
            return {
              ...item,
              discount,
              subtotal: item.price * item.quantity - discountAmount
            };
          }
          return item;
        })
      }));
    },

    /**
     * Leert Warenkorb
     */
    clear(): void {
      set({ items: [] });
    },

    /**
     * Gibt Item nach CartItem ID zurück
     */
    getItem(itemId: string): CartItem | undefined {
      let item: CartItem | undefined;
      const unsubscribe = subscribe((state) => {
        item = state.items.find((i) => i.id === itemId);
      });
      unsubscribe();
      return item;
    }
  };
}

export const cartStore = createCartStore();

/**
 * Derived Store: Warenkorb-Summe
 */
export const cartTotal = derived(cartStore, ($cart) => {
  return $cart.items.reduce((sum, item) => sum + item.subtotal, 0);
});

/**
 * Derived Store: Anzahl Artikel im Warenkorb
 */
export const cartItemCount = derived(cartStore, ($cart) => {
  return $cart.items.reduce((sum, item) => sum + item.quantity, 0);
});

/**
 * Derived Store: Formatierte Gesamtsumme
 */
export const cartTotalFormatted = derived(cartTotal, ($total) => formatCurrency($total));

